import { type NormalizedError, normalizeError } from './utils/normalize-error'

export default class PortalSdkThreadsClient {
    private readonly apiBase: string;
    constructor(private baseUrl = '', apiBase: string = '/api/chat') {
        this.apiBase = getPortalApiBase(apiBase);
    }
    async createThread(input: {
        workspaceId?: string;
        projectId?: string;
        createdByUserId?: string;
        packageName: string;
        systemName: string;
        title?: string;
        config?: {
            provider?: 'openrouter' | 'openai' | 'azure-openai' | 'flowise';
            model?: string;
            systemPrompt?: string;
            temperature?: number;
            defaultAgentId?: string;
            agents?: unknown;
            toolPolicy?: unknown;
            retentionDays?: number;
            metadata?: unknown;
        };
    }) {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
        const createThreadTimeoutMs = 30_000;
        const timeoutId = controller ? setTimeout(() => controller.abort(), createThreadTimeoutMs) : undefined;
        try {
            const response = await fetch(`${this.baseUrl}${this.apiBase}/threads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input),
                credentials: 'include',
                signal: controller?.signal,
            });
            if (timeoutId) clearTimeout(timeoutId);
            if (!response.ok) {
                const errorPayload = await this.readErrorPayload(response);
                const message = this.formatErrorMessage(response, errorPayload);
                const error = new Error(message) as NormalizedError;
                error.status = response.status;
                error.data = errorPayload ?? undefined;
                throw error;
            }
            return response.json() as Promise<unknown>;
        } catch (error: unknown) {
            const errorNormalized = normalizeError(error) as NormalizedError;
            if (timeoutId) clearTimeout(timeoutId);
            if (errorNormalized.name === 'AbortError') {
                throw new Error(`Thread creation timeout after ${createThreadTimeoutMs / 1000}s`);
            }
            throw errorNormalized;
        }
    }
    private async readErrorPayload(response: Response): Promise<Record<string, unknown> | null> {
        try {
            const data = await response.json() as Record<string, unknown>;
            if (data && typeof data === 'object') return data;
        } catch {
            // Ignore JSON parse errors and fall back
        }
        return null;
    }
    private formatErrorMessage(response: Response, payload: Record<string, unknown> | null): string {
        const fallback = `Failed to create thread: ${response.status}`;
        if (payload) {
            const errorField = payload.error;
            if (typeof errorField === 'string' && errorField.trim()) return errorField.trim();
        }
        return fallback;
    }
    private formatSendMessageError(response: Response, payload: Record<string, unknown> | null): string {
        const errorId = payload?.errorId ?? payload?.code;
        const errorIdSuffix = typeof errorId === 'string' ? ` (${errorId})` : '';
        const fallback = `Failed to send message: ${response.status}${errorIdSuffix}`;
        if (payload) {
            // Prefer server-provided details (e.g. actual exception message for admins)
            const details = payload.details;
            if (typeof details === 'string' && details.trim()) return `${details.trim()}${errorIdSuffix}`;
            const errorField = payload.error;
            if (typeof errorField === 'string' && errorField.trim()) return `${errorField.trim()}${errorIdSuffix}`;
        }
        return fallback;
    }
    private throwHttpError(operation: string, response: Response): never {
        const err = new Error(`Failed to ${operation}: ${response.status}`) as NormalizedError;
        err.status = response.status;
        throw err;
    }
    async getThread(threadId: string) {
        const response = await fetch(`${this.baseUrl}${this.apiBase}/threads/${threadId}`, { credentials: 'include' });
        if (!response.ok) this.throwHttpError('get thread', response);
        return response.json() as Promise<unknown>;
    }
    async getMessages(threadId: string, limit = 50, options?: { debug?: boolean }) {
        const params = new URLSearchParams({ limit: String(limit) });
        if (options?.debug) params.set('debug', 'true');
        const response = await fetch(`${this.baseUrl}${this.apiBase}/threads/${threadId}/messages?${params.toString()}`, {
            credentials: 'include'
        });
        if (!response.ok) this.throwHttpError('get messages', response);
        const data = await response.json();
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).messages)) {
            return (data as Record<string, unknown>).messages;
        }
        return data;
    }
    async listThreads(options?: { packageName?: string; limit?: number; cursor?: string; projectId?: string }) {
        const params = new URLSearchParams();
        if (options?.packageName) params.set('packageName', options.packageName);
        if (options?.limit) params.set('limit', options.limit.toString());
        if (options?.cursor) params.set('cursor', options.cursor);
        if (options?.projectId) params.set('projectId', options.projectId);
        const response = await fetch(`${this.baseUrl}${this.apiBase}/threads?${params.toString()}`, {
            method: 'GET',
            credentials: 'include'
        });
        if (!response.ok) throw new Error(`Failed to list threads: ${response.status}`);
        return response.json() as Promise<unknown>;
    }
    async sendMessage(threadId: string, content: string | { role: 'user'; content: string; }, options?: { clientMessageId?: string; agentId?: string; }) {
        const payload = typeof content === 'string'
            ? { role: 'user', content, clientMessageId: options?.clientMessageId, agentId: options?.agentId }
            : { ...content, clientMessageId: options?.clientMessageId, agentId: options?.agentId };
        const response = await fetch(`${this.baseUrl}${this.apiBase}/threads/${threadId}/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            credentials: 'include'
        });
        if (!response.ok) {
            const errorPayload = await this.readErrorPayload(response);
            const message = this.formatSendMessageError(response, errorPayload);
            const error = new Error(message) as NormalizedError;
            error.status = response.status;
            error.data = errorPayload ?? undefined;
            throw error;
        }
        return response.body as ReadableStream;
    }
    createStream(threadId: string) {
        return new EventSource(`${this.baseUrl}${this.apiBase}/threads/${threadId}/stream`);
    }
    async deleteThread(threadId: string) {
        const response = await fetch(`${this.baseUrl}${this.apiBase}/threads/${threadId}`, {
            method: 'DELETE',
            credentials: 'include'
        });
        if (!response.ok) throw new Error(`Failed to delete thread: ${response.status}`);
        return response.json() as Promise<unknown>;
    }
}

const PORTAL_BASE = '/api/portal';

function normalizePortalBase(apiBase: string): string {
    const normalized = apiBase.startsWith('/') ? apiBase : `/${apiBase}`;
    if (normalized === PORTAL_BASE) {
        throw new Error('Inline portal base requests are prohibited; please consume module-scoped `/api/<module>` endpoints.');
    }
    return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
}

export function getPortalApiBase(apiBase?: string): string {
    if (apiBase !== undefined && apiBase !== null && String(apiBase).trim().length > 0) {
        return normalizePortalBase(apiBase);
    }
    return '/api/chat';
}

export function useThread(threadId: string | null) {
    return {
        thread: null,
        messages: [],
        sendMessage: async (content: string) => {
            if (!threadId) throw new Error('No thread ID');
            const client = new PortalSdkThreadsClient();
            return client.sendMessage(threadId, content);
        },
        isLoading: false,
        error: null,
    };
}
